import { useEffect, useState } from 'react';
import { NavLink, Route, Routes, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserCheck, MessageSquare, FolderKanban,
  Newspaper, Hammer, MapPin, Settings, LogOut, ExternalLink, Bell, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import api from './api';
import DashboardPage from './DashboardPage';
import VisitorsPage from './VisitorsPage';
import LeadsPage from './LeadsPage';
import ChatInboxPage from './ChatInboxPage';
import ContentPage from './ContentPage';
import SettingsPage from './SettingsPage';
import SEOPage from './SEOPage';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/visitors', label: 'Visitors', icon: Users },
  { to: '/admin/leads', label: 'Leads', icon: UserCheck },
  { to: '/admin/chat', label: 'Live Chat', icon: MessageSquare, badge: 'chats' },
  { to: '/admin/seo', label: 'SEO', icon: Search },
  { to: '/admin/projects', label: 'Projects', icon: FolderKanban },
  { to: '/admin/blog', label: 'Blog', icon: Newspaper },
  { to: '/admin/services', label: 'Services', icon: Hammer },
  { to: '/admin/areas', label: 'Service Areas', icon: MapPin },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

const AdminApp = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [notif, setNotif] = useState({ unread: 0, items: [] });
  const [unreadChats, setUnreadChats] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('nbl_admin_token');
    if (!token) return navigate('/admin/login');
    api.get('/admin/me').then((r) => setUser(r.data)).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    let prevUnread = null;
    const poll = async () => {
      try {
        const [n, c] = await Promise.all([api.get('/admin/notifications'), api.get('/admin/chats')]);
        setNotif(n.data);
        setUnreadChats(c.data.reduce((acc, x) => acc + (x.unread_admin || 0), 0));
        if (prevUnread !== null && n.data.unread > prevUnread) {
          const fresh = n.data.items.find((i) => !i.read);
          if (fresh) toast.info(fresh.title, { description: 'New notification' });
        }
        prevUnread = n.data.unread;
      } catch (e) { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [user]);

  const logout = () => {
    localStorage.removeItem('nbl_admin_token');
    navigate('/admin/login');
  };

  if (!user) {
    return <div className="flex min-h-screen items-center justify-center bg-paper text-sm text-muted2">Loading…</div>;
  }

  return (
    <div className="flex min-h-screen bg-paper" data-testid="admin-shell">
      <aside className="fixed inset-y-0 left-0 z-40 flex w-60 flex-col bg-graphite text-paper">
        <div className="flex h-20 items-center border-b border-paper/10 px-5">
          <img src="/images/logo-light.png" alt="Nebraska Backyard Living" className="h-9 w-auto" />
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3" aria-label="Admin">
          {NAV.map(({ to, label, icon: Icon, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center justify-between rounded-md px-3.5 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? 'bg-bronze text-paper' : 'text-paper/60 hover:bg-paper/5 hover:text-paper'
                }`
              }
            >
              <span className="flex items-center gap-3"><Icon className="h-4.5 w-4.5 h-5 w-5" /> {label}</span>
              {badge === 'chats' && unreadChats > 0 && (
                <span data-testid="chat-unread-badge" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-paper px-1.5 text-[11px] font-bold text-graphite">
                  {unreadChats}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-paper/10 p-3">
          <Link to="/" data-testid="admin-view-site" className="flex items-center gap-3 rounded-md px-3.5 py-2.5 text-sm text-paper/60 transition-colors hover:text-paper">
            <ExternalLink className="h-5 w-5" /> View Website
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-md px-3.5 py-2.5 text-sm text-paper/60 transition-colors hover:text-paper">
            <LogOut className="h-5 w-5" /> Sign Out
          </button>
        </div>
      </aside>

      <div className="ml-60 flex-1">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-line bg-paper/90 px-8 backdrop-blur">
          <p className="text-sm text-muted2">Signed in as <span className="font-semibold text-ink">{user.name}</span></p>
          <div className="flex items-center gap-2 text-sm text-muted2">
            <Bell className="h-4 w-4" />
            {notif.unread > 0 ? (
              <span data-testid="notif-counter" className="font-semibold text-bronze">{notif.unread} unread notification{notif.unread === 1 ? '' : 's'}</span>
            ) : (
              <span data-testid="notif-counter">All caught up</span>
            )}
          </div>
        </header>
        <main className="p-8">
          <Routes>
            <Route index element={<DashboardPage />} />
            <Route path="visitors" element={<VisitorsPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="chat" element={<ChatInboxPage />} />
            <Route path="chat/:cid" element={<ChatInboxPage />} />
            <Route path="seo" element={<SEOPage />} />
            <Route path="projects" element={<ContentPage type="projects" />} />
            <Route path="blog" element={<ContentPage type="blog" />} />
            <Route path="services" element={<ContentPage type="services" />} />
            <Route path="areas" element={<ContentPage type="areas" />} />
            <Route path="settings" element={<SettingsPage user={user} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminApp;
